import React, { FormEvent, useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import { RestaurantPage } from '../../types/restaurantPersonnel';
import { extractApiErrorMessage } from '../../utils/apiError';

type Account = { id: string; userName: string };
type Props = {
  systemAdministrator: boolean; disabled: boolean;
  onSelect: (account: Account | null) => void; onBusyChange: (busy: boolean) => void;
};

export default function RestaurantAccountPicker({ systemAdministrator, disabled, onSelect, onBusyChange }: Props) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<RestaurantPage<Account> | null>(null);
  const [credentials, setCredentials] = useState({ userName: '', email: '', password: '' });
  const [selected, setSelected] = useState<Account | null>(null);
  const [created, setCreated] = useState<Account | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const generation = useRef(0);
  useEffect(() => () => { generation.current++; }, []);

  const find = async (page = 1) => {
    if (pending.current || disabled) return;
    const request = ++generation.current;
    pending.current = true;
    setBusy(true);
    onBusyChange(true);
    setError('');
    setResults(null);
    try {
      const response = await api.get<RestaurantPage<Account>>('/restaurant-personnel/accounts',
        { params: { search: search.trim(), page, pageSize: 10 } });
      if (request === generation.current) setResults(response.data);
    } catch (failure) {
      if (request === generation.current) setError(extractApiErrorMessage(failure, 'Не вдалося знайти обліковий запис.'));
    } finally {
      if (request === generation.current) { pending.current = false; setBusy(false); onBusyChange(false); }
    }
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (pending.current || disabled || created) return;
    const request = ++generation.current;
    pending.current = true;
    setBusy(true);
    onBusyChange(true);
    setError('');
    try {
      const response = await api.post<Account>('/restaurant-personnel/accounts', credentials);
      if (request !== generation.current) return;
      setCreated(response.data);
      setSelected(response.data);
      setCredentials({ userName: '', email: '', password: '' });
      onSelect(response.data);
    } catch (failure) {
      if (request === generation.current) setError(
        extractApiErrorMessage(failure, 'Не вдалося підтвердити створення облікового запису.') +
        ' Якщо відповідь втрачено, обліковий запис міг уже створитися. Знайдіть цей логін або email; не створюйте інший логін для повтору.');
    } finally {
      if (request === generation.current) { pending.current = false; setBusy(false); onBusyChange(false); }
    }
  };

  const locked = disabled || busy;
  return <fieldset className="restaurant-personnel__account-picker" disabled={locked}>
    <legend>Крок 1. Технічний обліковий запис для входу</legend>
    <p>Оберіть людині її власний вхід. Зміна ПІБ у картці не змінює обліковий запис.
      Права ресторану, ролі та зарплата автоматично не надаються.</p>
    {!systemAdministrator && <p>Тут доступні лише облікові записи, які вже мали працевлаштування в цьому ресторані.
      Для нового працівника зверніться до системного адміністратора.</p>}
    {selected && <p role="status">Обраний логін: <strong>{selected.userName}</strong> · ID: {selected.id}</p>}
    {created && <p role="status">Обліковий запис «{created.userName}» створено. Працевлаштування ще не збережено:
      заповніть і збережіть картку нижче. Якщо скасуєте картку, вхід залишиться; оберіть його через пошук, а не створюйте повторно.</p>}
    {systemAdministrator && <div className="restaurant-personnel__actions">
      <button type="button" aria-pressed={mode === 'existing'} onClick={() => setMode('existing')}>Обрати існуючий вхід</button>
      <button type="button" aria-pressed={mode === 'new'} disabled={!!created}
        onClick={() => { setMode('new'); setSelected(null); onSelect(null); }}>Створити новий вхід</button>
    </div>}
    {error && <p role="alert">{error}</p>}
    {busy && <p role="status">Обробка облікового запису…</p>}
    {mode === 'existing' && <>
      <form onSubmit={event => { event.preventDefault(); void find(); }}>
        <label>Логін або точний email<input type="search" required maxLength={200} value={search}
          onChange={event => { setSearch(event.target.value); setResults(null); }} /></label>
        <button type="submit">Знайти обліковий запис</button>
      </form>
      {results && <>
        <p role="status">Знайдено облікових записів: {results.total}</p>
        {!results.total && <p>Нічого не знайдено. Перевірте логін; новий вхід створює лише системний адміністратор.</p>}
        <div className="restaurant-personnel__list">
          {results.items.map(account => <button key={account.id} type="button"
            aria-pressed={selected?.id === account.id} onClick={() => { setSelected(account); onSelect(account); }}>
            Обрати логін: {account.userName} · ID: {account.id}
          </button>)}
        </div>
        <div className="restaurant-personnel__actions">
          <button type="button" disabled={results.page === 1} onClick={() => void find(results.page - 1)}>Попередні облікові записи</button>
          <button type="button" disabled={results.page * results.pageSize >= results.total}
            onClick={() => void find(results.page + 1)}>Наступні облікові записи</button>
        </div>
      </>}
    </>}
    {systemAdministrator && mode === 'new' && !created && <form onSubmit={create}>
      <p>Це окрема дія: створюється тільки вхід, без працевлаштування чи доступу до ресторану.
        Спочатку перевірте, чи людина вже має обліковий запис. Передайте пароль людині безпечним каналом.</p>
      <div className="restaurant-personnel__form">
        <label>Новий логін<input required maxLength={256} autoComplete="off" value={credentials.userName}
          onChange={event => setCredentials({ ...credentials, userName: event.target.value })} /></label>
        <label>Email нового входу<input required type="email" maxLength={256} autoComplete="off" value={credentials.email}
          onChange={event => setCredentials({ ...credentials, email: event.target.value })} /></label>
        <label>Пароль нового входу<input required type="password" maxLength={256} autoComplete="new-password" value={credentials.password}
          onChange={event => setCredentials({ ...credentials, password: event.target.value })} /></label>
      </div>
      <button type="submit">Створити обліковий запис</button>
    </form>}
  </fieldset>;
}
